import { useEffect, useState } from "react";
import { Button, Modal, Nav, Tab } from "react-bootstrap";

import { useAddCard, useEditCard } from "../hooks/useGetCardsTabAPI";

const defaultMessages = {
  Christmas: "Merry Christmas!",
  Birthday: "Happy Birthday!",
  "New Year": "Happy New Year!",
  "4th of July": "Happy 4th of July!",
};

const TypesOfCases = [
  { id: 1, name: "All Cases", value: "all" },
  { id: 2, name: "Open Cases", value: "open" },
  { id: 3, name: "Closed Cases", value: "closed" },
  { id: 4, name: "Manual selection of clients", value: "manual" },
];

const ageRangeOptions = [
  { id: "ageAll", name: "All", value: "all" },
  { id: "age0", name: "0-19 years", value: "0" },
  { id: "age1", name: "20-29 years", value: "1" },
  { id: "age2", name: "30-39 years", value: "2" },
  { id: "age3", name: "40-49 years", value: "3" },
  { id: "age4", name: "50-59 years", value: "4" },
  { id: "age5", name: "60+ years", value: "5" },
];

const emptyForm = {
  card_name: "",
  message: "",
  select_event: "",
  event_date: "",
  handwriting: "",
  handwritingName: "",
  cards: "",
  cardsName: "",
  case_type: "",
  ageRange: [],
};

const AddEditCardModal = ({
  showModal,
  handleClose,
  data,
  refetch,
  isEdit = false,
  filledData = {},
  editId = "",
}) => {
  const [activeTab, setActiveTab] = useState("card-info");
  const [modaldata, setModalData] = useState(emptyForm);

  const { saveCard } = useAddCard();
  const { editCard } = useEditCard();

  const labelStyle = { color: "var(--primary-25)", fontWeight: "600" };

  useEffect(() => {
    if (isEdit && filledData) {
      setModalData({
        ...emptyForm,
        card_name: filledData?.name || "",
        message: filledData?.message || "",
        select_event: filledData?.event_id || "",
        event_date: filledData?.date ?? "",
        handwriting: filledData?.handwriting_id || "",
        cards: filledData?.template_id || "",
        case_type: filledData?.case_type || "",
        ageRange: filledData?.age_ranges || [],
      });
    }
  }, [filledData]);

  const handleAgeRangeChange = (e) => {
    const { value, checked } = e.target;

    setModalData((prev) => {
      if (value === "all") {
        return { ...prev, ageRange: checked ? ["0", "1", "2", "3", "4", "5"] : [] };
      }

      const newAgeRange = checked
        ? [...prev.ageRange, value]
        : prev.ageRange.filter((age) => age !== value);

      const allSelected = ["0", "1", "2", "3", "4", "5"].every((age) => newAgeRange.includes(age));
      const ageAllEl = document.getElementById("ageAll");
      if (ageAllEl) ageAllEl.checked = allSelected;

      return { ...prev, ageRange: newAgeRange };
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "ageRange") {
      handleAgeRangeChange(e);
    } else {
      setModalData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleInputChangeMultipleValue = (e, nametype, nameId) => {
    const { name, value } = e.target;
    setModalData((prev) => ({ ...prev, [name]: value, [nametype]: nameId }));
  };

  const handleEventChange = (e) => {
    const selectedEventId = e.target.value;
    const selectedEvent = data?.card_events?.find(
      (event) => event.id.toString() === selectedEventId
    );
    setModalData((prev) => ({
      ...prev,
      select_event: selectedEventId,
      message:
        selectedEventId === "custom_event"
          ? ""
          : selectedEvent
            ? defaultMessages[selectedEvent.name] || ""
            : "",
    }));
  };

  const resetAndClose = () => {
    setModalData(emptyForm);
    setActiveTab("card-info");
    handleClose();
  };

  const handleSubmit = async () => {
    const initialPayload = {
      event: modaldata.select_event,
      date: modaldata.event_date,
      message: modaldata.message,
      card_name: modaldata.card_name,
    };

    if (isEdit) {
      initialPayload.editSelectedHandwriting = modaldata.handwriting;
      initialPayload.editSelectedCard = modaldata.cards;
      initialPayload.edit_case_type = modaldata.case_type;
      initialPayload.editageRange = modaldata.ageRange;
    } else {
      initialPayload.selectedHandwriting = modaldata.handwriting;
      initialPayload.selectedCard = modaldata.cards;
      initialPayload.case_type = modaldata.case_type;
      initialPayload.ageRange = modaldata.ageRange;
    }

    const payload = Object.fromEntries(
      Object.entries(initialPayload).filter(([, value]) => {
        if (value === undefined || value === null || value === "") return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      })
    );

    try {
      if (isEdit) {
        payload.card_id = Number(editId);
        data?.handwritings.forEach((handwriting) => {
          if (modaldata.handwriting === handwriting.id) {
            payload.editSelectedHandwriting = `${modaldata.handwriting}-${handwriting.name}`;
          }
        });
        data?.cards.forEach((card) => {
          if (modaldata.cards === card.id) {
            payload.editSelectedCard = `${modaldata.cards}-${card.name}`;
          }
        });
        if (modaldata.case_type === "manual") delete payload.editageRange;
        await editCard(JSON.stringify(payload));
      } else {
        payload.selectedHandwriting = `${modaldata.handwriting}-${modaldata.handwritingName}`;
        payload.selectedCard = `${modaldata.cards}-${modaldata.cardsName}`;
        if (modaldata.case_type === "manual") delete payload.ageRange;
        await saveCard(JSON.stringify(payload));
      }

      refetch();
      resetAndClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      dialogClassName="modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
      size="lg"
    >
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
      >
        <Modal.Title
          className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
          style={{ fontSize: "14px", fontWeight: "600" }}
        >
          {isEdit ? "Edit Card" : "New Card"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "0px" }}>
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Nav
            variant="tabs"
            className="justify-content-around"
            style={{ marginTop: "5px", marginBottom: "5px" }}
          >
            <Nav.Link
              className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-before"
              eventKey="card-info"
            >
              1. Card Information
            </Nav.Link>
            <Nav.Link
              className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite"
              eventKey="handwriting"
            >
              2. Handwriting
            </Nav.Link>
            <Nav.Link
              className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite"
              eventKey="template"
            >
              3. Template
            </Nav.Link>
            <Nav.Link
              className="new-provider-nav-link-hover-effect nav-link Pad8 tab-ite apply-after"
              eventKey="clients"
            >
              4. Clients
            </Nav.Link>
          </Nav>

          <Tab.Content
            style={{
              height: "550px",
              overflow: "scroll",
            }}
          >
            {/* ===================== CARD INFO TAB ===================== */}
            <Tab.Pane eventKey="card-info">
              <div className="custom-tab m-b-5">
                <div className="row mx-auto align-items-center m-b-5 mb-3">
                  <div className="col-md-2 p-l-5 pe-0 text-start">
                    <span className="d-inline-block" style={labelStyle}>
                      Card Name:{" "}
                    </span>
                  </div>
                  <div className="col-md-10 p-l-5 p-r-5">
                    <input
                      type="text"
                      name="card_name"
                      value={modaldata.card_name}
                      onChange={handleInputChange}
                      className="form-control height-25 p-l-5 p-r-5 p-t-5 p-b-5"
                      placeholder="Enter Card Name"
                    />
                  </div>
                </div>

                <div className="row mx-auto align-items-center m-b-5 mb-3">
                  <div className="col-md-2 p-l-5 pe-0 text-start">
                    <span className="d-inline-block" style={labelStyle}>
                      Select Event:{" "}
                    </span>
                  </div>
                  <div className="col-md-5 p-l-5 p-r-5">
                    <select
                      value={modaldata.select_event}
                      onChange={handleEventChange}
                      className="form-select height-25 p-l-5 p-r-5 pt-0 pb-0"
                      style={{ borderRadius: "0px", borderColor: "#ced4da" }}
                    >
                      <option value="">Select an Event</option>
                      <option value="custom_event">Custom Event</option>
                      {data?.card_events?.map((event) => (
                        <option key={event.id} value={event.id}>
                          {event.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {modaldata.select_event === "custom_event" && (
                    <>
                      <div className="col-md-2 p-l-5 pe-0 text-start">
                        <span className="d-inline-block" style={labelStyle}>
                          Event Date:{" "}
                        </span>
                      </div>
                      <div className="col-md-3 p-l-5 p-r-5">
                        <input
                          type="date"
                          name="event_date"
                          value={modaldata.event_date}
                          onChange={handleInputChange}
                          className="form-control height-25 p-l-5 p-r-5 pt-0 pb-0"
                          style={{ borderRadius: "0px" }}
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="row mx-auto align-items-center m-b-5 mb-3">
                  <div className="col-md-2 p-l-5 pe-0 text-start">
                    <span className="d-inline-block" style={labelStyle}>
                      Message:{" "}
                    </span>
                  </div>
                  <div className="col-md-10 p-l-5 p-r-5">
                    <input
                      type="text"
                      name="message"
                      value={modaldata.message}
                      onChange={handleInputChange}
                      className="form-control height-25 p-l-5 p-r-5 p-t-5 p-b-5"
                      placeholder="Enter Message"
                    />
                  </div>
                </div>
              </div>
            </Tab.Pane>

            {/* ===================== HANDWRITING TAB ===================== */}
            <Tab.Pane eventKey="handwriting">
              <div className="custom-tab m-b-5">
                <div
                  className="d-flex align-items-center p-l-5"
                  style={{ flexWrap: "wrap", gap: "10px" }}
                >
                  {data?.handwritings.map((handwriting) => (
                    <div
                      key={handwriting.id}
                      className="hand-writing-item"
                      style={{ width: "300px" }}
                    >
                      <img src={handwriting?.preview_url} alt={handwriting?.name} />
                      <span className="d-flex align-items-center justify-content-center">
                        {handwriting?.name}
                        <input
                          type="radio"
                          name="handwriting"
                          className="m-l-5"
                          checked={modaldata.handwriting === handwriting.id}
                          value={handwriting.id}
                          onChange={(e) =>
                            handleInputChangeMultipleValue(e, "handwritingName", handwriting.name)
                          }
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Tab.Pane>

            {/* ===================== TEMPLATE TAB ===================== */}
            <Tab.Pane
              eventKey="template"
              className="invisible-scrollbar"
              style={{
                scrollbarWidth: "none",
              }}
            >
              <div className="custom-tab m-b-5">
                <div
                  className="d-flex align-items-center invisible-scrollbar p-l-5"
                  style={{ flexWrap: "wrap", gap: "15px", scrollbarWidth: "none" }}
                >
                  {data?.cards.map((card) => (
                    <div key={card.id} className="hand-writing-item" style={{ width: "200px" }}>
                      <img src={card?.preview_url} alt={card?.name} />
                      <span className="d-flex align-items-center justify-content-center">
                        {card?.name}
                        <input
                          type="radio"
                          name="cards"
                          className="m-l-5"
                          value={card.id}
                          checked={modaldata.cards === card.id}
                          onChange={(e) =>
                            handleInputChangeMultipleValue(e, "cardsName", card.name)
                          }
                        />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Tab.Pane>

            {/* ===================== CLIENTS TAB ===================== */}
            <Tab.Pane eventKey="clients">
              <div className="custom-tab m-b-5">
                <div className="row mx-auto align-items-start m-b-5 mb-3">
                  <div className="col-md-6 p-l-5 p-r-5">
                    <span className="d-inline-block m-b-5" style={labelStyle}>
                      Types of Cases:
                    </span>
                    {TypesOfCases.map((item) => (
                      <label
                        key={item.id}
                        className="d-flex mb-0 align-items-center m-b-5"
                        style={{ gap: "5px", cursor: "pointer" }}
                      >
                        <input
                          type="radio"
                          name="case_type"
                          value={item.value}
                          checked={modaldata.case_type === item.value}
                          onChange={handleInputChange}
                          style={{ width: "12px", height: "12px" }}
                        />
                        <span>{item.name}</span>
                      </label>
                    ))}
                  </div>

                  {modaldata.case_type !== "manual" && (
                    <div className="col-md-6 p-l-5 p-r-5">
                      <span className="d-inline-block m-b-5" style={labelStyle}>
                        Select Age Range:
                      </span>
                      {ageRangeOptions.map((item) => (
                        <label
                          key={item.id}
                          className="d-flex mb-0 align-items-center m-b-5"
                          style={{ gap: "5px", cursor: "pointer" }}
                        >
                          <input
                            type="checkbox"
                            name="ageRange"
                            id={item.id}
                            value={item.value}
                            checked={
                              item.value === "all"
                                ? ["0", "1", "2", "3", "4", "5"].every((a) =>
                                    modaldata.ageRange.includes(a)
                                  )
                                : modaldata.ageRange.includes(item.value)
                            }
                            onChange={handleAgeRangeChange}
                            className="form-check mb-0"
                            style={{ width: "12px", height: "12px", minHeight: "12px" }}
                          />
                          <span>{item.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider"
        style={{ borderTop: "none" }}
      >
        <Button
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          variant="secondary"
          onClick={resetAndClose}
        >
          Cancel
        </Button>
        <Button
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          variant="primary"
          onClick={handleSubmit}
        >
          {isEdit ? "Update Card" : "Save New Card"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditCardModal;
